import { Box, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
	useSearchSuppliers,
	type SupplierSearchResult,
} from '../hooks/useSearchSuppliers';
import { TextField } from '../ui';
import {
	CheckboxMenuList,
	MenuRow,
	menuSurfaceSx,
} from '../ui/primitives/CheckboxMenu';

export type { SupplierSearchResult };

const DEFAULT_EMPTY = 'No suppliers found. If your supplier isn’t listed, please contact us.';

export interface SupplierSearchResultsProps {
	/** Already-debounced query. Search runs once this is longer than two characters. */
	query: string;
	onSelect: (supplier: SupplierSearchResult) => void;
	excludeIds?: Array<string>;
	selectedId?: string;
	emptyMessage?: string;
	maxH?: string;
}

/** Loading / error / empty / list for a supplier-directory query. */
export const SupplierSearchResults = ({
	query,
	onSelect,
	excludeIds,
	selectedId,
	emptyMessage = DEFAULT_EMPTY,
	maxH = '240px',
}: SupplierSearchResultsProps) => {
	const { data, isLoading, error } = useSearchSuppliers(query);
	const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
	const suppliers = (data?.suppliers ?? []).filter((supplier) => !excluded.has(supplier._id));

	if (query.length <= 2) return null;

	if (isLoading) {
		return (
			<Box py={4} textAlign="center">
				<Spinner size="sm" color="fg.muted" />
				<Text fontSize="sm" color="fg.muted" mt={2}>
					Searching…
				</Text>
			</Box>
		);
	}

	if (error) {
		return (
			<Box px={3} py={3}>
				<Text fontSize="sm" color="error.fg">
					Error searching suppliers
				</Text>
			</Box>
		);
	}

	if (suppliers.length === 0) {
		return (
			<Box px={3} py={3}>
				<Text fontSize="sm" color="fg.muted">
					{emptyMessage}
				</Text>
			</Box>
		);
	}

	return (
		<CheckboxMenuList maxH={maxH}>
			{suppliers.map((supplier) => (
				<MenuRow
					key={supplier._id}
					kind="single"
					isSelected={supplier._id === selectedId}
					description={supplier.formattedAddress || undefined}
					onClick={() => onSelect(supplier)}
				>
					{supplier.title}
				</MenuRow>
			))}
		</CheckboxMenuList>
	);
};

export interface SupplierSearchFieldProps {
	onSelect: (supplier: SupplierSearchResult) => void;
	placeholder?: string;
	autoFocus?: boolean;
	excludeIds?: Array<string>;
	emptyMessage?: string;
}

/**
 * Type-three-characters supplier lookup. Selecting a result is the only way to
 * name a supplier — the directory is keyed by id, so names cannot be free-typed.
 */
export const SupplierSearchField = ({
	onSelect,
	placeholder = 'Search for a supplier...',
	autoFocus = false,
	excludeIds,
	emptyMessage,
}: SupplierSearchFieldProps) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedTerm, setDebouncedTerm] = useState('');
	const [showResults, setShowResults] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	const isOpen = showResults && searchTerm.length > 2;

	return (
		<Box position="relative">
			<TextField
				size="sm"
				h="36px"
				bg="white"
				autoFocus={autoFocus}
				value={searchTerm}
				placeholder={placeholder}
				onChange={(e) => {
					setSearchTerm(e.target.value);
					setShowResults(e.target.value.length > 2);
				}}
				onFocus={() => searchTerm.length > 2 && setShowResults(true)}
				onBlur={() => setTimeout(() => setShowResults(false), 150)}
			/>
			{isOpen && (
				<Box
					position="absolute"
					top="100%"
					left={0}
					right={0}
					mt={1}
					zIndex={20}
					maxH="240px"
					overflowY="auto"
					sx={menuSurfaceSx}
					onMouseDown={(event) => event.preventDefault()}
				>
					<SupplierSearchResults
						query={debouncedTerm}
						excludeIds={excludeIds}
						emptyMessage={emptyMessage}
						onSelect={(supplier) => {
							onSelect(supplier);
							setSearchTerm('');
							setShowResults(false);
						}}
					/>
				</Box>
			)}
		</Box>
	);
};

export default SupplierSearchField;
